import {
  type DynamicClient,
  MFAAction,
  type WalletAccount,
} from '@dynamic-labs-sdk/client';
import {
  assertDefined,
  consumeMfaTokenIfRequiredForAction,
  getSignedSessionId,
} from '@dynamic-labs-sdk/client/core';
import type { WaasProvider } from '@dynamic-labs-sdk/client/waas/core';
import type { Hex } from 'viem';
import { parseSignature } from 'viem';
import type { SignAuthorizationReturnType } from 'viem/accounts';
import { hashAuthorization } from 'viem/utils';

import type { SignAuthorizationParametersWithoutPrivateKey } from '../../../../viem/viem.types';

type SignAuthorizationParams = {
  parameters: SignAuthorizationParametersWithoutPrivateKey;
  password?: string;
  waasProvider: WaasProvider;
  walletAccount: WalletAccount;
};

/** @not-instrumented */
// eslint-disable-next-line custom-rules/require-single-object-param
export const signAuthorization = async (
  {
    parameters,
    walletAccount,
    waasProvider,
    password,
  }: SignAuthorizationParams,
  dynamicClient: DynamicClient
): Promise<SignAuthorizationReturnType> => {
  const { address: contractAddress, chainId, nonce } = parameters;

  assertDefined(contractAddress, 'Contract address not found');

  const { signature: signedSessionId } = await getSignedSessionId(
    dynamicClient
  );

  const mfaToken = await consumeMfaTokenIfRequiredForAction(
    { mfaAction: MFAAction.WalletWaasSign },
    dynamicClient
  );

  const waasClient = await waasProvider.getWaasClient();

  const authorization = {
    address: contractAddress,
    chainId,
    nonce,
  };

  const signature = await waasClient.signRawMessage({
    accountAddress: walletAccount.address,
    authToken: dynamicClient.token ?? undefined,
    context: { eip7702Auth: authorization },
    message: hashAuthorization(authorization).slice(2),
    mfaToken,
    password,
    signedSessionId,
  });

  const parsedSignature = parseSignature(signature as Hex);

  return {
    address: contractAddress,
    chainId,
    nonce,
    r: parsedSignature.r,
    s: parsedSignature.s,
    v: parsedSignature.v,
    yParity: parsedSignature.yParity,
  };
};
